import { MouseEvent, RefObject, ReactNode } from "react";

import { AutofillIndicator } from "./autofill-components";
import {
  AutofillFailureBanner,
  ConnectionBanner,
  LanguageBanner,
  SectionButtons,
  SectionHeading,
  SectionForm,
  PageWrapper,
  Page,
  Section,
} from "./section-components";
import { PreviewFormFooter } from "./shared-components/preview-form-footer";
import {
  AutoFillStatus,
  ConnectionItem,
  SectionErrors,
  SectionItem,
  TeamItem,
  Toast,
} from "../types";

export type IntakeSectionProps = {
  team: TeamItem;
  connection?: ConnectionItem;
  autoFillSection?: SectionItem;
  sections: SectionItem[];
  selected: SectionItem;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  onPreviousButton?: () => void;
  onNextButton?: (e: MouseEvent<HTMLButtonElement>) => void;
  onSubmitButton?: (e: MouseEvent<HTMLButtonElement>) => void;
  onSubmit?: () => void;
  onReset?: () => void;
  children?: ReactNode;
  errors?: SectionErrors;
  errorsRef?: RefObject<HTMLDivElement | null>;
  submitError?: string;
  isForm?: boolean;
  previewErrorToggle?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  retrySubmitResponses: () => void;
  autoFillStatus?: AutoFillStatus;
  loading?: boolean;
  aiFillFromMatterInfo?: boolean;
  isPreview?: boolean;
  setToasts?: (toasts: Toast[]) => void;
  isTeamHidden?: boolean;
  isWebEnquiry?: boolean;
  isPrint?: boolean;
  selectedLanguage?: string;
  setSelectedLanguage?: (language: string) => void;
  languageOptions?: Array<{ label: string; value: string }>;
  voiceConversation?: ReactNode;
  isMobile?: boolean;
  hasDigitalSignature?: boolean;
};

const noop = () => {};

export const IntakeSection = ({
  team,
  connection,
  autoFillSection,
  sections,
  selected,
  sidebar,
  setSidebar,
  onPreviousButton,
  onNextButton,
  onSubmitButton,
  onSubmit,
  onReset,
  children,
  submitError,
  errors,
  errorsRef,
  isForm = true,
  previewErrorToggle,
  retrySubmitResponses,
  autoFillStatus,
  loading,
  aiFillFromMatterInfo,
  isPreview,
  setToasts,
  isTeamHidden,
  isWebEnquiry,
  isPrint,
  selectedLanguage,
  setSelectedLanguage,
  languageOptions,
  voiceConversation,
  isMobile,
  hasDigitalSignature,
}: IntakeSectionProps) => {
  const validationAlert = () => {
    if (previewErrorToggle && setToasts && errors && !previewErrorToggle[0]) {
      setToasts([
        {
          text: "Turn on 'Ignore Required Fields' to preview without validation.",
          palette: "warning",
          width: "medium",
          actions: [
            {
              text: "Turn On",
              onClick: () => previewErrorToggle[1](true),
            },
          ],
        },
      ]);
    }
    return noop();
  };

  const selectedIndex = sections.findIndex((s) => s.uri === selected?.uri);

  const buttons = {
    onPrevious:
      selectedIndex > 0 || (selectedIndex === 0 && autoFillSection)
        ? onPreviousButton || validationAlert
        : undefined,
    onNext: sections.length > 1 ? onNextButton || validationAlert : undefined,
    onSubmit:
      sections.length === 1 ? onSubmitButton || validationAlert : undefined,
  };

  const isLoadingBeforeAutoFill = loading && aiFillFromMatterInfo;
  const autoFillInProgress = autoFillStatus === "filling";
  const showSectionsButton = !!(sections.length > 1 || autoFillSection);
  const sectionToolbarShown = !!(
    isPreview ||
    showSectionsButton ||
    buttons.onNext ||
    buttons.onPrevious
  );
  const showVoiceInSectionHeading =
    !!voiceConversation &&
    ((isMobile && !sectionToolbarShown) ||
      (!isMobile && !!isTeamHidden && !sectionToolbarShown));

  return (
    <PageWrapper sidebar={sidebar}>
      <LanguageBanner
        isTeamHidden={isTeamHidden}
        selectedLanguage={selectedLanguage}
      />
      {!isWebEnquiry && (
        <ConnectionBanner
          connectionError={connection?.status === "error"}
          retry={retrySubmitResponses}
          selectedLanguage={selectedLanguage}
        />
      )}
      <AutofillFailureBanner
        autoFillFailed={autoFillStatus === "failed"}
        selectedLanguage={selectedLanguage}
      />
      {(autoFillStatus || isLoadingBeforeAutoFill) && (
        <AutofillIndicator
          status={autoFillStatus}
          loading={isLoadingBeforeAutoFill}
        />
      )}
      <Page
        isPreview={isPreview}
        className={previewErrorToggle && "mb-[5.6rem]"}
      >
        <Section>
          <SectionForm onSubmit={onSubmit} onReset={onReset} isForm={isForm}>
            <SectionButtons
              isForm={isForm}
              isPreview={isPreview}
              team={team}
              hasError={submitError}
              hasDigitalSignature={hasDigitalSignature}
              setSidebar={setSidebar}
              {...buttons}
              disabled={{
                submit: autoFillInProgress || isLoadingBeforeAutoFill,
                back: autoFillInProgress || isLoadingBeforeAutoFill,
                next: autoFillInProgress || isLoadingBeforeAutoFill,
              }}
              showSectionsButton={showSectionsButton}
              isTeamHidden={isTeamHidden}
              setToasts={setToasts}
              isPrint={isPrint}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              languageOptions={languageOptions}
              voiceConversation={voiceConversation}
              isMobile={isMobile}
            >
              <SectionHeading
                section={selected}
                errors={errors}
                errorsRef={errorsRef}
                hideErrors={previewErrorToggle?.[0]}
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                languageOptions={languageOptions}
                showLanguageSelector={
                  (isTeamHidden || isMobile) &&
                  !sectionToolbarShown &&
                  !isPreview
                }
                showVoiceInSectionHeading={showVoiceInSectionHeading}
                voiceConversation={voiceConversation}
                isMobile={isMobile}
              />
              <div className="py-[5.6rem]">{children}</div>
            </SectionButtons>
          </SectionForm>
        </Section>
      </Page>
      {previewErrorToggle && (
        <PreviewFormFooter previewErrorToggle={previewErrorToggle} />
      )}
    </PageWrapper>
  );
};
